import { format, startOfWeek, addDays } from 'date-fns'

import { ITemplate, ITemplateTimeslot } from '@/_types/workplace'

/**
 * Count of working days in a Monday–Friday work week.
 *
 * @remarks Used with {@link calculateWorkWeek} to derive Friday from Monday via `addDays`.
 */
export const WORK_WEEK_DAYS = 5

/**
 * Default calendar settings: Monday-based weeks and 7:00–19:00 hour bounds where applied.
 *
 * @remarks `WEEK_STARTS_ON` is the date-fns `weekStartsOn` value (1 = Monday).
 */
export const CALENDAR_DEFAULTS = {
	WEEK_STARTS_ON: 1, // Monday (date-fns `weekStartsOn`)
	START_HOUR: 7,
	END_HOUR: 19
} as const

/**
 * Inclusive date range for API or UI as `yyyy-MM-dd` strings (typically a work week).
 *
 * @property dateFrom - First day of the range (e.g. Monday).
 * @property dateTo - Last day of the range (e.g. Friday).
 */
interface DateRange {
	dateFrom: string
	dateTo: string
}

/**
 * Returns the first timeslot in a template that has a valid `startTime`.
 *
 * @param template - Workplace `ITemplate`; `timeslots` must be pre-sorted nearest-first (first item = earliest).
 * @returns The first `ITemplateTimeslot` with `startTime`, or `null` if there are no slots or the first slot lacks `startTime`.
 */
function findEarliestAvailableSlot(template: ITemplate): ITemplateTimeslot | null {
	if (!template?.timeslots?.length) {
		return null
	}

	const firstSlot = template.timeslots[0]

	if (!firstSlot?.startTime) {
		console.warn('Template slot is missing startTime', {
			template: template.id,
			slot: firstSlot
		})
		return null
	}

	return firstSlot
}

/**
 * Computes the Monday–Friday work week that contains `targetDate`, as `yyyy-MM-dd` strings.
 *
 * @param targetDate - Any `Date` in the week you care about. Invalid dates fall back to “now”.
 * @returns `{ dateFrom, dateTo }` — Monday and Friday of that week per {@link CALENDAR_DEFAULTS.WEEK_STARTS_ON}.
 */
function calculateWorkWeek(targetDate: Date): DateRange {
	if (!targetDate || isNaN(targetDate.getTime())) {
		console.warn('Invalid target date provided, using current date', targetDate)
		targetDate = new Date()
	}

	const monday = startOfWeek(targetDate, { weekStartsOn: CALENDAR_DEFAULTS.WEEK_STARTS_ON })
	const friday = addDays(monday, WORK_WEEK_DAYS - 1)

	return {
		dateFrom: format(monday, 'yyyy-MM-dd'),
		dateTo: format(friday, 'yyyy-MM-dd')
	}
}

/**
 * Finds a template in a list by numeric id.
 *
 * @param templates - List of `ITemplate` (e.g. from workplace); may be `undefined` or empty.
 * @param templateId - Primary key of the template; must be a valid number.
 * @returns The matching `ITemplate`, or `null` if the list is missing/empty, the id is invalid, or no match.
 */
function findTemplateById(templates: ITemplate[] | undefined, templateId: number): ITemplate | null {
	if (!templates?.length) {
		return null
	}

	if (typeof templateId !== 'number' || !Number.isFinite(templateId)) {
		console.warn('Invalid templateId provided', templateId)
		return null
	}

	return templates.find(template => template.id === templateId) ?? null
}

/**
 * Chooses the initial calendar week: the work week containing the earliest slot for the template, or the current week.
 *
 * @param templates - All templates for the context (same as {@link findTemplateById}).
 * @param templateId - Selected template id used to resolve the template and its earliest slot.
 * @returns A `{ dateFrom, dateTo }` range (Monday–Friday) for that week; on errors, the current week.
 */
export function calculateOptimalCalendarWeek(
	templates: ITemplate[] | undefined,
	templateId: number
): DateRange {
	try {
		const template = findTemplateById(templates, templateId)
		const earliestSlot = template ? findEarliestAvailableSlot(template) : null

		const targetDate = earliestSlot ? new Date(earliestSlot.startTime) : new Date()

		return calculateWorkWeek(targetDate)
	} catch (error) {
		console.error('Error calculating optimal calendar week', { templates, templateId, error })
		return calculateWorkWeek(new Date())
	}
}
